import { useQueryClient } from '@tanstack/react-query';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectByIdQuery } from '@/features/projects/hooks/projects';
import {
  shouldGroupErroredWithFailed,
  shouldPreferFileNameOverDisplayName,
} from '@/features/shared/utils/projectStatusPresentation';
import { queryKeys } from '@/shared/hooks';
import type { ProjectDto, QueryResult } from '@flowqa/shared';

const resolveCachedProject = ({
  orgId,
  projectId,
  queryClient,
}: {
  orgId: string | null;
  projectId: string | null;
  queryClient: ReturnType<typeof useQueryClient>;
}): ProjectDto | null => {
  if (!projectId) {
    return null;
  }

  const projectById =
    queryClient.getQueryData<ProjectDto | null>(queryKeys.projectById(projectId, orgId)) ?? null;
  if (projectById) {
    return projectById;
  }

  const scopedProjects = queryClient.getQueryData<QueryResult<ProjectDto>>(
    queryKeys.projects(orgId),
  );
  return scopedProjects?.items.find((project) => project.id === projectId) ?? null;
};

export const useProjectStatusPresentation = (): {
  groupErroredWithFailed: boolean;
  preferFileNameOverDisplayName: boolean;
} => {
  const { selectedProjectId } = useProjectFilter();
  const { selectedOrganisationId } = useOrganisationFilter();
  const queryClient = useQueryClient();
  const projectQuery = useProjectByIdQuery(selectedProjectId, {
    refetchOnWindowFocus: false,
    staleTimeMs: 60_000,
  });
  const cachedProject = resolveCachedProject({
    orgId: selectedOrganisationId ?? null,
    projectId: selectedProjectId,
    queryClient,
  });
  const effectiveProject = projectQuery.data ?? cachedProject;

  return {
    groupErroredWithFailed: shouldGroupErroredWithFailed(
      effectiveProject,
      selectedProjectId ?? null,
    ),
    preferFileNameOverDisplayName: shouldPreferFileNameOverDisplayName(
      effectiveProject,
      selectedProjectId ?? null,
    ),
  };
};
